/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CustomTasksPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, History, FileCode, Clock, CheckCircle2, XCircle, AlertCircle, Trash2, Edit } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import Modal from '../../components/ui/Modal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { fetchCustomTasks, deleteCustomTask, executeCustomTask, fetchTaskExecutions, CustomTask, CustomTaskExecution } from './api';

interface ExecutionResult {
    success: boolean;
    exitCode: number;
    output: string;
    error?: string;
    duration: number;
}

export default function CustomTasksPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [data, setData] = useState<CustomTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({ page: 1, limit: 10 });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const [executingTaskId, setExecutingTaskId] = useState<number | null>(null);
    const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
    const [showExecutionModal, setShowExecutionModal] = useState(false);

    const [historyData, setHistoryData] = useState<CustomTaskExecution[]>([]);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; task: CustomTask | null }>({
        isOpen: false,
        task: null
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchCustomTasks({
                page: pagination.page,
                limit: pagination.limit,
                search: searchQuery
            });
            setData(response.data);
            setTotal(response.pagination?.total || 0);
        } catch (error) {
            showError(error.message || t('customTasks.errors.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [pagination, searchQuery, showError, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreate = () => navigate('/admin/maintenance/tasks/new');
    const handleEdit = (task: CustomTask) => navigate('/admin/maintenance/tasks/' + task.id);

    const handleDelete = (task: CustomTask) => {
        setDeleteModal({ isOpen: true, task });
    };

    const confirmDelete = async () => {
        if (!deleteModal.task) return;
        try {
            await deleteCustomTask(deleteModal.task.id);
            showSuccess(t('customTasks.messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, task: null });
            fetchData();
        } catch (error) {
            showError(error.message || t('customTasks.errors.deleteFailed'));
        }
    };

    const handleExecute = async (task: CustomTask) => {
        setExecutingTaskId(task.id);
        try {
            const result = await executeCustomTask(task.id);
            setExecutionResult({
                success: result.status === 'success',
                exitCode: result.exitCode || 0,
                output: result.output || '',
                error: result.errorMessage || undefined,
                duration: result.durationMs || 0
            });
            setShowExecutionModal(true);
            fetchData();
        } catch (error) {
            showError(error.message || t('customTasks.errors.executionFailed'));
        } finally {
            setExecutingTaskId(null);
        }
    };

    const handleViewHistory = async (task: CustomTask) => {
        setLoadingHistory(true);
        setShowHistoryModal(true);
        try {
            const result = await fetchTaskExecutions(task.id, 20);
            setHistoryData(result);
        } catch (error) {
            showError(error.message || t('customTasks.errors.historyFailed'));
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSort = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    const formatDuration = (ms: number) => {
        if (ms < 1000) return ms + 'ms';
        const seconds = Math.floor(ms / 1000);
        if (seconds < 60) return seconds + 's';
        const minutes = Math.floor(seconds / 60);
        return minutes + 'm ' + (seconds % 60) + 's';
    };

    const columns = useMemo(() => [
        {
            key: 'name',
            label: t('customTasks.fields.name'),
            sortable: true,
            visible: true,
            render: (_: unknown, item: CustomTask) => (
                <div className="flex items-center gap-2">
                    <FileCode className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-primary">{item.name}</span>
                </div>
            )
        },
        {
            key: 'scriptPath',
            label: t('customTasks.fields.scriptPath'),
            sortable: true,
            visible: true,
            render: (_: unknown, item: CustomTask) => (
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{item.scriptPath}</code>
            )
        },
        {
            key: 'cronProfile',
            label: t('customTasks.fields.cronProfile'),
            sortable: false,
            visible: true,
            render: (_: unknown, item: CustomTask) => (
                item.cronProfile ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                        <Clock className="h-3 w-3" />
                        {item.cronProfile.name}
                    </span>
                ) : (
                    <span className="text-muted-foreground text-sm">{t('customTasks.labels.manual')}</span>
                )
            )
        },
        {
            key: 'lastStatus',
            label: t('customTasks.fields.lastRunStatus'),
            sortable: true,
            visible: true,
            render: (_: unknown, item: CustomTask) => {
                if (!item.lastRun) {
                    return <span className="text-muted-foreground text-sm">{t('customTasks.labels.neverRun')}</span>;
                }
                const isSuccess = item.lastStatus === 'success';
                return (
                    <div className="flex items-center gap-2">
                        {isSuccess ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                        <span className={isSuccess ? 'text-green-600' : 'text-red-600'}>
                            {isSuccess ? t('customTasks.status.success') : t('customTasks.status.failed')}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'enabled',
            label: t('customTasks.fields.enabled'),
            sortable: true,
            visible: true,
            render: (_: unknown, item: CustomTask) => (
                <span className={"inline-flex items-center px-2 py-1 rounded-full text-xs font-medium " + (item.enabled ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400')}>
                    {item.enabled ? t('customTasks.labels.enabled') : t('customTasks.labels.disabled')}
                </span>
            )
        },
        {
            key: 'actions',
            label: t('common.actions', 'Actions'),
            sortable: false,
            visible: true,
            render: (_: unknown, item: CustomTask) => (
                <div className="flex items-center gap-1">
                    <SimpleTooltip content={t('customTasks.actions.execute')} position="top">
                        <button
                            onClick={() => handleExecute(item)}
                            disabled={executingTaskId === item.id}
                            className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-md transition-colors disabled:opacity-50"
                        >
                            <Play className={"h-4 w-4 text-green-600 dark:text-green-400" + (executingTaskId === item.id ? ' animate-spin' : '')} />
                        </button>
                    </SimpleTooltip>
                    <SimpleTooltip content={t('customTasks.actions.viewHistory')} position="top">
                        <button
                            onClick={() => handleViewHistory(item)}
                            className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-md transition-colors"
                        >
                            <History className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </button>
                    </SimpleTooltip>
                    <SimpleTooltip content={t('common.edit', 'Edit')} position="top">
                        <button
                            onClick={() => handleEdit(item)}
                            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                        >
                            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </button>
                    </SimpleTooltip>
                    <SimpleTooltip content={t('common.delete', 'Delete')} position="top">
                        <button
                            onClick={() => handleDelete(item)}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors"
                        >
                            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </button>
                    </SimpleTooltip>
                </div>
            )
        }
    ], [executingTaskId, t]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={data}
                loading={loading}
                columns={columns}
                pagination={{ ...pagination, total, totalPages: Math.ceil(total / pagination.limit) }}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={setSearchQuery}
                onSort={handleSort}
                onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                onItemsPerPageChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
                onColumnToggle={() => { }}
                title={t('customTasks.title')}
                titleIcon={<FileCode className="h-5 w-5 text-primary" />}
                actions={
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
                    >
                        <Plus className="h-4 w-4" />
                        <span>{t('customTasks.actions.add')}</span>
                    </button>
                }
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, task: null })}
                onConfirm={confirmDelete}
                title={t('customTasks.dialogs.delete.title')}
                message={t('customTasks.dialogs.delete.description')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
            />

            <Modal
                isOpen={showExecutionModal}
                onClose={() => setShowExecutionModal(false)}
                title={t('customTasks.dialogs.executionResult.title')}
                size="lg"
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                        {executionResult?.success ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
                        <span className="text-muted-foreground">{executionResult?.success ? t('customTasks.dialogs.executionResult.successMessage') : t('customTasks.dialogs.executionResult.failedMessage')}</span>
                    </div>
                    <div className="flex gap-4">
                        <div><span className="text-sm text-muted-foreground">{t('customTasks.fields.exitCode')}:</span><span className={"ml-2 px-2 py-1 rounded text-xs font-medium " + (executionResult?.exitCode === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')}>{executionResult?.exitCode}</span></div>
                        <div><span className="text-sm text-muted-foreground">{t('customTasks.fields.duration')}:</span><span className="ml-2 font-mono">{executionResult ? formatDuration(executionResult.duration) : '-'}</span></div>
                    </div>
                    <div>
                        <span className="text-sm text-muted-foreground block mb-2">{t('customTasks.fields.output')}:</span>
                        <div className="h-64 w-full overflow-auto rounded border bg-muted/50 p-4">
                            <pre className="text-xs font-mono whitespace-pre-wrap">{executionResult?.output || t('customTasks.labels.noOutput')}{executionResult?.error && <span className="text-red-500">{"\n\n--- STDERR ---\n"}{executionResult.error}</span>}</pre>
                        </div>
                    </div>
                    <div className="flex justify-end pt-4">
                        <button onClick={() => setShowExecutionModal(false)} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90">{t('customTasks.actions.close')}</button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                title={t('customTasks.dialogs.history.title')}
                size="xl"
            >
                <div className="space-y-4">
                    {loadingHistory ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : historyData.length === 0 ? (
                        <div className="text-center py-12">
                            <History className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                            <p className="text-muted-foreground">{t('customTasks.messages.noExecutions')}</p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-lg border border-border">
                            <table className="w-full">
                                <thead className="bg-muted/50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('customTasks.columns.status')}</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('customTasks.columns.lastRun')}</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('customTasks.fields.duration')}</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('customTasks.fields.exitCode')}</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('customTasks.triggers.manual')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border bg-card">
                                    {historyData.map((exec) => (
                                        <tr key={exec.id} className="hover:bg-accent/50 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {exec.status === 'success' ? (
                                                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                                                    ) : exec.status === 'failed' ? (
                                                        <XCircle className="h-4 w-4 text-red-500" />
                                                    ) : exec.status === 'running' ? (
                                                        <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                                                    )}
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${exec.status === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                                            exec.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                                                exec.status === 'running' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                                                                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                        }`}>
                                                        {t('customTasks.status.' + exec.status)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-foreground">
                                                {new Date(exec.startedAt).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-mono text-muted-foreground">
                                                {exec.durationMs ? formatDuration(exec.durationMs) : '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-mono ${exec.exitCode === 0 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                                        exec.exitCode !== null ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                                                            'bg-muted text-muted-foreground'
                                                    }`}>
                                                    {exec.exitCode ?? '-'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-muted-foreground">
                                                <span className="inline-flex items-center gap-1.5">
                                                    {exec.triggeredBy === 'cron' && <Clock className="h-3 w-3" />}
                                                    {exec.triggeredBy === 'manual' && <Play className="h-3 w-3" />}
                                                    {t('customTasks.triggers.' + (exec.triggeredBy || 'manual'))}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="flex justify-end pt-2 border-t border-border">
                        <button
                            onClick={() => setShowHistoryModal(false)}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                        >
                            {t('customTasks.actions.close')}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
